import axios, { AxiosInstance, AxiosResponse, AxiosError, InternalAxiosRequestConfig } from 'axios';
import { API_BASE_URL } from '@config/api';

/**
 * Client API unificat pentru toate serviciile
 * Include interceptors pentru autentificare și gestionarea erorilor
 */
export const apiClient: AxiosInstance = axios.create({
  baseURL: API_BASE_URL,
  timeout: 15000,
  headers: {
    'Content-Type': 'application/json',
  },
});

function readCsrfTokenFromCookie(): string | null {
  if (typeof document === 'undefined') return null;
  const match = document.cookie.match(/(?:^|;\s*)csrf_token=([^;]*)/);
  return match ? decodeURIComponent(match[1]) : null;
}

/**
 * Interceptor pentru request-uri - adaugă token-ul de autentificare
 */
apiClient.interceptors.request.use(
  (config: InternalAxiosRequestConfig) => {
    if (config.data instanceof FormData && config.headers) {
      delete config.headers['Content-Type'];
    }
    const token = localStorage.getItem('token');
    if (token && config.headers) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    const method = config.method?.toLowerCase();
    if (method && ['post', 'put', 'patch', 'delete'].includes(method) && config.headers) {
      const csrf = readCsrfTokenFromCookie();
      if (csrf) {
        config.headers['X-CSRF-Token'] = csrf;
      }
    }
    return config;
  },
  (error: AxiosError) => {
    return Promise.reject(error);
  }
);

/**
 * Interceptor pentru response-uri - gestionează refresh token și erori
 */
apiClient.interceptors.response.use(
  (response: AxiosResponse) => {
    return response;
  },
  async (error: AxiosError) => {
    const originalRequest = error.config as InternalAxiosRequestConfig & { _retry?: boolean };
    const status = error?.response?.status;

    // Handle network errors și NGHTTP2_PROTOCOL_ERROR cu retry
    if (error.code === 'ERR_NETWORK' || error.message?.includes('NGHTTP2_PROTOCOL_ERROR')) {
      if (originalRequest && !originalRequest._retry) {
        originalRequest._retry = true;
        await new Promise(resolve => setTimeout(resolve, 1000)); // Wait 1 second
        return apiClient(originalRequest);
      }
    }

    // Refresh token logic pentru 401
    if (status === 401 && originalRequest && !originalRequest._retry) {
      originalRequest._retry = true;

      try {
        const refreshToken = localStorage.getItem('refreshToken');
        if (!refreshToken) {
          throw new Error('No refresh token available');
        }

        const refreshResponse = await apiClient.post('/auth/refresh', {
          refreshToken: refreshToken,
        });

        const refreshData = refreshResponse.data?.data;
        if (refreshResponse.data?.success && refreshData?.token) {
          const newToken = refreshData.token;
          const newRefreshToken = refreshData.refreshToken;
          
          localStorage.setItem('token', newToken);
          if (newRefreshToken) {
            localStorage.setItem('refreshToken', newRefreshToken);
          }

          // Retry original request cu noul token
          if (originalRequest.headers) {
            originalRequest.headers.Authorization = `Bearer ${newToken}`;
          }
          return apiClient(originalRequest);
        }
      } catch (refreshError) {
        // Refresh failed - force logout
        localStorage.removeItem('token');
        localStorage.removeItem('refreshToken');
        localStorage.removeItem('user');
        localStorage.removeItem('originalUser');
        localStorage.removeItem('isImpersonating');
        window.dispatchEvent(new Event('force-logout'));
        if (typeof window !== 'undefined') {
          window.location.href = '/login';
        }
        return Promise.reject(refreshError);
      }
    }

    // Gestionare erori 401 (neautorizat)
    if (status === 401) {
      localStorage.removeItem('token');
      localStorage.removeItem('refreshToken');
      localStorage.removeItem('user');
      localStorage.removeItem('originalUser');
      localStorage.removeItem('isImpersonating');
      if (typeof window !== 'undefined') {
        window.location.href = '/login';
      }
    }

    return Promise.reject(error);
  }
);

/**
 * Export default pentru compatibilitate
 */
export default apiClient;

